import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import CloseIcon from '@material-ui/icons/Close';
import { useIssueComments } from '../../hooks/useIssueComments';
import { Comment } from '../../types';

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(2, 0),
  },
  commentForm: {
    marginBottom: theme.spacing(3),
  },
  commentField: {
    marginBottom: theme.spacing(1),
  },
  submitButton: {
    backgroundColor: '#dc3545',
    color: 'white',
    '&:hover': {
      backgroundColor: '#c82333',
    },
    '&:disabled': {
      backgroundColor: '#6c757d',
    },
  },
  helperText: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    marginBottom: theme.spacing(1),
  },
  commentCard: {
    marginBottom: theme.spacing(2),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  commentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  commentMeta: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  commentActions: {
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  commentText: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}));

interface CommentsComponentProps {
  host: string;
  issueId: string;
  onCommentAdded?: () => void;
  refreshTrigger?: number;
}

export const CommentsComponent: React.FC<CommentsComponentProps> = ({
  host,
  issueId,
  onCommentAdded,
  refreshTrigger,
}) => {
  const classes = useStyles();
  const [newComment, setNewComment] = useState('');
  const [editingComment, setEditingComment] = useState<Comment | null>(null);
  const [editText, setEditText] = useState('');
  const [error, setError] = useState('');

  const MAX_LENGTH = 10000;

  const {
    comments,
    isLoading,
    error: fetchError,
    addComment,
    updateComment,
    deleteComment,
    isAdding,
    isUpdating,
    isDeleting,
    refetch,
  } = useIssueComments({ host, id: issueId });

  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      refetch();
    }
  }, [refreshTrigger, refetch]);

  const handleNewCommentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    // Only update if within limit
    if (newValue.length <= MAX_LENGTH) {
      setNewComment(newValue);
      if (error) setError('');
    }
  };

  const handleEditTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    // Only update if within limit
    if (newValue.length <= MAX_LENGTH) {
      setEditText(newValue);
    }
  };

  const getHelperText = (text: string, hasError?: boolean, errorText?: string) => {
    if (hasError && errorText) return errorText;
    
    const remaining = MAX_LENGTH - text.length;
    const characterCount = `${text.length.toLocaleString()}/${MAX_LENGTH.toLocaleString()} characters`;
    
    if (remaining === 0) {
      return `${characterCount} - Character limit reached`;
    } else if (remaining < 100) {
      return `${characterCount} - ${remaining} remaining`;
    } else {
      return characterCount;
    }
  };

  const handleSubmitComment = async () => {
    if (!newComment.trim()) {
      setError('Comment text is required');
      return;
    }

    if (newComment.length > MAX_LENGTH) {
      setError(`Comment exceeds maximum length of ${MAX_LENGTH.toLocaleString()} characters`);
      return;
    }

    try {
      await addComment(newComment.trim());
      setNewComment('');
      setError('');
      
      // Call the callback to notify parent component
      onCommentAdded?.();
    } catch (err) {
      setError('Failed to add comment. Please try again.');
      console.error('Error adding comment:', err);
    }
  };

  const handleEditComment = (comment: Comment) => {
    setEditingComment(comment);
    setEditText(comment.data.text);
  };

  const handleUpdateComment = async () => {
    if (!editingComment) return;

    if (!editText.trim()) {
      return;
    }

    if (editText.length > MAX_LENGTH) {
      return;
    }

    try {
      await updateComment(editingComment.id, editText.trim());
      setEditingComment(null);
      setEditText('');
      
      // Call the callback to notify parent component
      onCommentAdded?.();
    } catch (err) {
      console.error('Error updating comment:', err);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (window.confirm('Are you sure you want to delete this comment?')) {
      try {
        await deleteComment(commentId);
        
        // Call the callback to notify parent component
        onCommentAdded?.();
      } catch (err) {
        console.error('Error deleting comment:', err);
      }
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZoneName: 'longOffset',
    });
  };

  if (fetchError) {
    return (
      <Box className={classes.container}>
        <Typography color="error">
          Error loading comments: {fetchError.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      {/* Add Comment Form */}
      <Box className={classes.commentForm}>
        <TextField
          className={classes.commentField}
          label="Comment*"
          placeholder="Add a comment..."
          multiline
          rows={4}
          variant="outlined"
          fullWidth
          value={newComment}
          onChange={handleNewCommentChange}
          error={!!error}
          helperText={getHelperText(newComment, !!error, error)}
          disabled={isAdding}
          inputProps={{
            maxLength: MAX_LENGTH,
          }}
        />
        <Button
          className={classes.submitButton}
          variant="contained"
          onClick={handleSubmitComment}
          disabled={isAdding || !newComment.trim()}
        >
          {isAdding ? 'Submitting...' : 'Submit Comment'}
        </Button>
      </Box>

      <Divider />

      {/* Comments List */}
      <Box mt={2}>
        {isLoading ? (
          <Typography>Loading comments...</Typography>
        ) : comments.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography variant="body2">
              No comments yet. Be the first to comment!
            </Typography>
          </Box>
        ) : (
          comments.map((comment) => (
            <Card key={comment.id} className={classes.commentCard} variant="outlined">
              <CardContent>
                <Box className={classes.commentHeader}>
                  <Box className={classes.commentMeta}>
                    {/* <Typography component="span" style={{ fontWeight: 'bold', marginRight: 8 }}>
                      {comment.user.email}
                    </Typography> */}
                    <Typography component="span">
                      {formatDate(comment.dateCreated)}
                    </Typography>
                  </Box>
                  
                  <Box className={classes.commentActions}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditComment(comment)}
                      disabled={isUpdating || isDeleting}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteComment(comment.id)}
                      disabled={isUpdating || isDeleting}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                
                <Typography className={classes.commentText}>
                  {comment.data.text}
                </Typography>
              </CardContent>
            </Card>
          ))
        )}
      </Box>

      {/* Edit Comment Dialog */}
      <Dialog 
        open={!!editingComment} 
        onClose={() => setEditingComment(null)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle className={classes.dialogTitle}>
          <Typography variant="h6">Edit Comment</Typography>
          <IconButton onClick={() => setEditingComment(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent>
          <TextField
            label="Comment*"
            multiline
            rows={4}
            variant="outlined"
            fullWidth
            value={editText}
            onChange={handleEditTextChange}
            helperText={getHelperText(editText)}
            disabled={isUpdating}
            inputProps={{
              maxLength: MAX_LENGTH,
            }}
          />
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setEditingComment(null)} disabled={isUpdating}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateComment}
            variant="contained"
            color="primary"
            disabled={isUpdating || !editText.trim()}
          >
            {isUpdating ? 'Updating...' : 'Update Comment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};